/* SidePaneRouter — 보조 패널 전용 라우터(#1471).
 *
 * 보조 패널은 주 화면과 **같은 라우트 표**(App.jsx appRoutes)를 그리되, 자기만의 MemoryRouter 안에서 그린다.
 * 그래서 보조 패널 안의 navigate·Link·useSearchParams·useParams·가드(MenuRoute·PrivateRoute)의 Navigate 는
 * 이 메모리 라우터에만 걸린다 — 보조 패널에서 계약을 눌러도 주 화면 주소는 그대로다.
 *
 * 왜 이렇게 감싸나: react-router 6.30.4 는 라우터 안에 라우터를 두면 던진다(«You cannot render a <Router>
 * inside another <Router>»). 그 판정이 useContext(LocationContext) != null 하나라, 바깥 위치 문맥을 null 로
 * 끊으면 안쪽 MemoryRouter 가 독립 라우터가 된다. RouteContext 도 비운다 — 바깥 <Route> 의 matches 를 안쪽
 * <Routes> 가 부모로 읽지 않게(바깥이 "/main/*" 같은 하위 라우트여도 안쪽 "/a" 가 매칭된다).
 * 🔴 package.json 의 react-router(-dom) 메이저가 바뀌면 이 파일과 SidePaneRouter.test.jsx 를 먼저 본다
 *    (UNSAFE_ 문맥 이름·초기값이 바뀔 수 있다).
 *
 * 자기 앱 밖으로 나가면(§3-1): 판정은 **렌더 단계**에서 한다 — 바깥 라우트가 보조 패널 안에서 한 번이라도
 * 마운트되면 EmbedActivator 언마운트의 deactivate() 가 주 화면 메신저를 비우고, /home·/admin 은 API·청크를
 * 부른다. 그래서 자기 앱이 아니면 라우트 표 대신 null 을 그리고, effect 에서 onEscape 로 넘긴다.
 *   1. 자기 앱(getCurrentMainApp === sideApp) → 그대로 · onLocationChange(pathname + search)
 *   2. /login(세션 만료 가드) → onEscape('/login')
 *   3. /home(권한 없음 가드) → onEscape(null) — 보조 패널만 닫는다(주 화면은 그대로)
 *   4. 그 밖 → onEscape(pathname + search + hash[, state])
 *   onEscape 가 없으면(시험 등) 2~4 는 아무것도 하지 않고 라우트 표를 그대로 그린다.
 *
 * 알려진 한계(이 파일 밖):
 *   - window.location·window.history.pushState 를 직접 쓰는 곳(useConversation.js `?conv=` · MeetingsPage ·
 *     ChatPage·BoardPage 의 window.location)은 라우터 밖이라 보조 패널에서도 주 화면 주소를 바꾼다.
 *   - 주 화면이 농심DAS 작업 화면(PdasJobPage)이면 스캔 칸이 포커스를 되가져가 보조 패널 입력을 빼앗는다
 *     (pdas 를 보조 패널 대상에서 뺀 것은 반대쪽만 막는다).
 */
import { useEffect, useRef } from 'react';
import { MemoryRouter, UNSAFE_LocationContext, UNSAFE_RouteContext, useLocation } from 'react-router-dom';
import { getCurrentMainApp } from './SplitShell';

// react-router 6 RouteContext 의 기본값과 같은 모양.
const EMPTY_ROUTE_CONTEXT = { outlet: null, matches: [], isDataRoute: false };

function PaneGate({ sideApp, onEscape, onLocationChange, children }) {
  const location = useLocation();
  const own = !!sideApp && getCurrentMainApp(location.pathname) === sideApp;
  const escaping = !!onEscape && !own;

  // 콜백은 ref 로 읽는다 — 위치가 바뀔 때만 한 번 부르게(콜백이 새로 만들어져도 다시 부르지 않게).
  const escapeRef = useRef(onEscape);
  const changeRef = useRef(onLocationChange);
  useEffect(() => {
    escapeRef.current = onEscape;
    changeRef.current = onLocationChange;
  });

  useEffect(() => {
    const { pathname, search, hash, state } = location;
    if (own) {
      if (changeRef.current) changeRef.current(`${pathname}${search}`);
      return;
    }
    const leave = escapeRef.current;
    if (!leave) return;
    if (pathname === '/login') leave('/login');
    else if (pathname === '/home') leave(null);
    else if (state !== undefined && state !== null) leave(`${pathname}${search}${hash}`, state);
    else leave(`${pathname}${search}${hash}`);
  }, [location.key]);

  return escaping ? null : children;
}

export default function SidePaneRouter({ sideApp, initialPath, onEscape, onLocationChange, children }) {
  return (
    <UNSAFE_LocationContext.Provider value={null}>
      <UNSAFE_RouteContext.Provider value={EMPTY_ROUTE_CONTEXT}>
        <MemoryRouter initialEntries={[initialPath || '/']}>
          <PaneGate sideApp={sideApp} onEscape={onEscape} onLocationChange={onLocationChange}>
            {children}
          </PaneGate>
        </MemoryRouter>
      </UNSAFE_RouteContext.Provider>
    </UNSAFE_LocationContext.Provider>
  );
}
